<template>
    <div class="dashboard-layout">
        <header class="header">
            <h1 @click="router.push('/')">Dashboard</h1>
            <GlobalSearch :route-map="ROUTE_MAP" />
            <CButton class="border" @click="logout">Déconnexion</CButton>
        </header>

        <section class="sidebar">
            <nav class="nav">
                <router-link to="/ticket-boards">
                    <img src="@ezio/assets/images/main-nav/tickets-on.svg" class="on" />
                    <img src="@ezio/assets/images/main-nav/tickets-off.svg" class="off" />
                    <span>Tickets</span>
                </router-link>

                <router-link to="/organizations">
                    <img src="@ezio/assets/images/main-nav/organisation-on.svg" class="on" />
                    <img src="@ezio/assets/images/main-nav/organisation-off.svg" class="off" />
                    <span>Organisations</span>
                </router-link>

                <router-link to="/agencies">
                    <img src="@ezio/assets/images/main-nav/home-on.svg" class="on" />
                    <img src="@ezio/assets/images/main-nav/home-off.svg" class="off" />
                    <span>Établissements</span>
                </router-link>

                <router-link to="/networks">
                    <img src="@ezio/assets/images/main-nav/network-on.svg" class="on" />
                    <img src="@ezio/assets/images/main-nav/network-off.svg" class="off" />
                    <span>Réseaux</span>
                </router-link>
                <router-link to="/bank">
                    <img src="@ezio/assets/images/main-nav/bank-on.svg" class="on" />
                    <img src="@ezio/assets/images/main-nav/bank-off.svg" class="off" />
                    <span>Bancaire</span>
                </router-link>
                <router-link to="/users">
                    <img src="@ezio/assets/images/main-nav/managers-on.svg" class="on" />
                    <img src="@ezio/assets/images/main-nav/managers-off.svg" class="off" />
                    <span>Utilisateurs</span>
                </router-link>
                <router-link to="/permission-catalog">
                    <span>Permissions</span>
                </router-link>
            </nav>
            <div class="logo">
                <img src="@ezio/assets/images/ezio/Logo-EZIO.png" alt="" />
            </div>
        </section>

        <router-view />
    </div>
</template>

<script lang="ts" setup>
import { reset } from '@/stores'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import GlobalSearch from '@ezio/features/search/components/GlobalSearch.vue'

const router = useRouter()

const ROUTE_MAP: Record<string, (id: string) => RouteLocationRaw> = {
    Agency: (id) => ({ name: 'agency', params: { id } }),
    Card: (id) => ({ name: 'card', params: { id } }),
    Transaction: (id) => ({ name: 'card-transactions-show', params: { transactionId: id } }),
    Client: (id) => ({ name: 'client', params: { id } }),
    Employee: (id) => ({ name: 'employee', params: { id } }),
    Manager: (id) => ({ name: 'manager', params: { id } })
}

const logout = () => {
    reset()
    router.replace('/sign-in')
}
</script>

<style lang="scss" scoped>
.dashboard-layout {
    display: grid;
    grid-template-columns: 120px 1fr;
    grid-template-rows: 70px 1fr;

    width: 100%;
    height: 100vh;
    overflow: hidden;

    @media screen and (max-width: 930px) {
        grid-template-rows: 70px auto 1fr;
        grid-template-columns: 1fr;
    }
}

.header {
    grid-column: 1 / -1;
    position: relative;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 50px;
    background: #fff;
    border-bottom: 1px solid var(--c-color-border-soft);

    @media screen and (max-width: 930px) {
        padding: 0 20px;
    }

    h1 {
        color: var(--c-color-primary);
        font-weight: 700;
        font-size: 1.2rem;
        cursor: pointer;
        font-family: 'Sora', sans-serif;
    }
}

/* === SIDEBAR & NAV (NOUVELLE CHARTE) === */
.sidebar {
    position: relative;
    z-index: 50;
    background: #fff;
    box-shadow: 2px 0 12px rgba(0, 0, 0, 0.04);
    border-right: 1px solid var(--c-color-border-soft);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 24px;

    @media screen and (max-width: 930px) {
        padding-top: 5px;
        padding-bottom: 5px;
    }

    .logo {
        margin-top: auto;
        padding-bottom: 24px;
        opacity: 0.4;
        display: flex;
        justify-content: center;
        width: 100%;

        img {
            display: block;
            width: 60px;
        }
        @media screen and (max-width: 930px) {
            display: none;
        }
    }
}

.nav {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 100%;

    a {
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 5px;
        padding: 12px 12px 10px;
        border-radius: 10px;
        width: 100px;

        font-family: 'Sora', sans-serif;
        color: #204d4f;
        font-size: 10.5px;
        font-weight: 500;
        text-decoration: none;
        transition: all 0.2s ease;
        position: relative;

        img {
            width: 32px;
            height: 32px;
            margin-bottom: 0;
            transition: transform 0.2s ease;
        }

        .on {
            display: none;
        }

        &:hover {
            background: rgba(32, 77, 79, 0.06);
            transform: translateY(-1px);

            img {
                transform: scale(1.08);
            }
        }

        &:active {
            transform: translateY(0);
        }

        &.router-link-active {
            background: color-mix(in srgb, var(--c-color-accent) 8%, transparent);
            color: var(--c-color-accent);
            box-shadow: 0 2px 8px color-mix(in srgb, var(--c-color-accent) 12%, transparent);

            &::before {
                content: '';
                position: absolute;
                left: 0;
                top: 50%;
                transform: translateY(-50%);
                width: 3px;
                height: 24px;
                background: var(--c-color-accent);
                border-radius: 0 3px 3px 0;
            }

            .on {
                display: block;
            }
            .off {
                display: none;
            }
        }
    }

    @media screen and (max-width: 930px) {
        flex-direction: row;
        justify-content: space-evenly;
        padding: 0 20px;

        a {
            width: auto;
            padding: 10px;
            background: transparent;
            box-shadow: none;
            transform: none;

            &::before {
                display: none;
            }

            &.router-link-active {
                background: color-mix(in srgb, var(--c-color-accent) 8%, transparent);
                color: var(--c-color-accent);
                border-bottom: none;
                border-radius: 10px;
            }
            span {
                display: none;
            }
        }
    }
}
</style>
